import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import ChatNotification from '../components/ChatNotification';
import { View, StyleSheet, Platform } from 'react-native';
import { useAuth } from '../services/AuthContext';
import { getUserById } from '../services/database';
import { supabase } from '../services/supabase';

const ChatNotificationContext = createContext();

export const useChatNotification = () => {
  const context = useContext(ChatNotificationContext);
  if (!context) {
    throw new Error('useChatNotification must be used within a ChatNotificationProvider');
  }
  return context;
};

export const ChatNotificationProvider = ({ children, navigationRef }) => {
  const [notifications, setNotifications] = useState([]);
  const { appUser } = useAuth();
  const subscriptionRef = useRef(null);
  const activeConversationIdRef = useRef(null);

  const showChatNotification = useCallback((notificationData) => {
    // Suppress notification if the user is currently viewing this conversation
    if (activeConversationIdRef.current && activeConversationIdRef.current === notificationData.conversationId) {
      console.log('🔕 Suppressing notification for active conversation:', notificationData.conversationId);
      return;
    }

    const notification = {
      id: Date.now() + Math.random(), // Unique ID
      senderId: notificationData.senderId,
      senderName: notificationData.senderName || 'Someone',
      senderAvatar: notificationData.senderAvatar,
      message: notificationData.message || '',
      conversationId: notificationData.conversationId,
      timestamp: new Date(),
    };

    setNotifications((prev) => {
      // Remove any existing notification from the same sender
      const filtered = prev.filter((n) => n.senderId !== notification.senderId);
      // Add new notification at the beginning
      return [notification, ...filtered].slice(0, 5); // Max 5 notifications
    });
  }, []);

  const dismissNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  const handleNotificationPress = useCallback((notification) => {
    // Store navigation handler - will be set by ChatNotification component
    if (notification.onPress) {
      notification.onPress(notification);
    }
  }, []);

  // Track which conversation the user is currently viewing
  const setActiveConversationId = useCallback((conversationId) => {
    activeConversationIdRef.current = conversationId;
  }, []);

  // App-wide subscription to new messages for toast notifications
  useEffect(() => {
    const userId = appUser?.id;
    if (!userId) {
      return;
    }

    // Clean up previous subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    console.log('🔔 Setting up app-wide chat notification subscription for user:', userId);

    // Use a unique channel name to avoid collision with per-screen subscriptions
    const channel = supabase
      .channel(`app-notifications-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${userId}`,
        },
        async (payload) => {
          const newMessage = payload.new;

          // Suppress if user is viewing this conversation
          if (activeConversationIdRef.current === newMessage.conversation_id) {
            console.log('🔕 Suppressing notification - user is viewing this conversation');
            return;
          }

          try {
            const sender = await getUserById(newMessage.sender_id);
            if (sender) {
              showChatNotification({
                senderId: sender.id,
                senderName: sender.username || sender.club_name || 'Someone',
                senderAvatar: sender.profile_picture_url,
                message: newMessage.content,
                conversationId: newMessage.conversation_id,
              });
            }
          } catch (error) {
            console.error('Error showing chat notification:', error);
          }
        }
      )
      .subscribe();

    subscriptionRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [appUser?.id, showChatNotification]);

  return (
    <ChatNotificationContext.Provider value={{ showChatNotification, setActiveConversationId }}>
      {children}
      {/* Render notifications */}
      <View style={styles.notificationContainer} pointerEvents="box-none">
        {notifications.map((notification, index) => (
          <ChatNotification
            key={notification.id}
            notification={notification}
            visible={true}
            index={index}
            navigationRef={navigationRef}
            onDismiss={() => dismissNotification(notification.id)}
            onPress={handleNotificationPress}
          />
        ))}
      </View>
    </ChatNotificationContext.Provider>
  );
};

const styles = StyleSheet.create({
  notificationContainer: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'box-none',
    zIndex: 10000,
  },
});

export default ChatNotificationProvider;
